import React, { useEffect, useState } from "react";
import { CheckCircle, XCircle, StickyNote, Loader2 } from "lucide-react";
import { getCandidateAvailabilities } from "@/api/candidateAvailability";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const today = new Date();

const getStartOfWeek = (baseDate) => {
    const start = new Date(baseDate);
    start.setDate(start.getDate() - start.getDay() + 1);
    start.setHours(0, 0, 0, 0);
    return start;
};

const getDateLabel = (dayOffset, startOfWeek) => {
    const date = new Date(startOfWeek);
    date.setDate(date.getDate() + dayOffset);
    return {
        day: days[dayOffset],
        date: `${date.getDate()} ${date.toLocaleString("default", { month: "short" })} ${date.getFullYear()}`
    };
};

export default function CandidateAvailabilityTable({ showFilters = false }) {
    const [data, setData] = useState([]);
    const [search, setSearch] = useState("");
    const [availableOn, setAvailableOn] = useState({
        monday: false,
        tuesday: false,
        wednesday: false,
        thursday: false,
        friday: false,
        saturday: false,
        sunday: false
    });
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [weekStart, setWeekStart] = useState(getStartOfWeek(today));
    const pageSize = 50;

    const shortToLongDayMap = {
        Mon: "monday",
        Tue: "tuesday",
        Wed: "wednesday",
        Thu: "thursday",
        Fri: "friday",
        Sat: "saturday",
        Sun: "sunday"
    };

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            const selectedDays = Object.entries(availableOn)
                .filter(([_, value]) => value)
                .map(([key]) => key);
            const results = await getCandidateAvailabilities({
                week_starting: weekStart,
                search: search,
                days: selectedDays
            });
            setData(results);
            setPage(1);
            setLoading(false);
        };
        fetchData();
    }, [search, availableOn, weekStart]);

    const handleSwitchChange = (day) => {
        setAvailableOn((prev) => ({ ...prev, [day]: !prev[day] }));
    };

    const filteredData = data.slice((page - 1) * pageSize, page * pageSize);
    const totalPages = Math.ceil(data.length / pageSize);

    return (
        <div className="space-y-4">
            {showFilters && (
                <div className="p-4 bg-white border rounded-lg shadow-sm space-y-4">
                    <div>
                        <Label htmlFor="search" className="text-sm font-medium text-gray-700 block mb-1">
                            Candidate
                        </Label>
                        <Input
                            id="search"
                            placeholder="Search by candidate name"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="w-full"
                        />
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-7 gap-4">
                        {Object.keys(availableOn).map((day) => (
                            <div key={day} className="flex items-center space-x-2">
                                <Switch id={day} checked={availableOn[day]} onCheckedChange={() => handleSwitchChange(day)} />
                                <Label htmlFor={day} className="capitalize text-sm">
                                    {day}
                                </Label>
                            </div>
                        ))}
                    </div>
                    <div className="flex gap-2">
                        <Button variant={weekStart.getTime() === getStartOfWeek(today).getTime() ? "default" : "outline"} onClick={() => setWeekStart(getStartOfWeek(today))}>
                            This Week
                        </Button>
                        <Button variant={weekStart.getTime() === getStartOfWeek(new Date(today.getTime() + 7 * 86400000)).getTime() ? "default" : "outline"} onClick={() => setWeekStart(getStartOfWeek(new Date(today.getTime() + 7 * 86400000)))}>
                            Next Week
                        </Button>
                    </div>
                </div>
            )}

            {loading ? (
                <div className="flex justify-center items-center h-64">
                    <Loader2 className="animate-spin w-8 h-8 text-[#5D1F42]" />
                </div>
            ) : (
                <div className="overflow-auto border rounded-xl shadow bg-white max-h-[75vh] transition-opacity duration-300 opacity-100">
                    <table className="min-w-full border-collapse">
                        <thead className="sticky top-0 bg-gray-100 z-10">
                        <tr>
                            <th className="text-left px-4 py-3 border-b border-r font-medium text-[#5D1F42] bg-gray-100">Candidate</th>
                            {days.map((_, i) => {
                                const { day, date } = getDateLabel(i, weekStart);
                                return (
                                    <th
                                        key={i}
                                        className="text-center px-2 py-2 border-b border-r font-medium text-[#5D1F42] bg-gray-100 whitespace-nowrap"
                                    >
                                        <div>{day}</div>
                                        <div className="text-xs text-gray-500">{date}</div>
                                    </th>
                                );
                            })}
                        </tr>
                        </thead>
                        <tbody>
                        {filteredData.map((row, idx) => (
                            <tr key={row.candidate} className={idx % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                                <td className="px-4 py-2 border-r font-medium text-[#5D1F42] whitespace-nowrap">
                                    {row.candidateName}
                                </td>
                                {days.map((day, i) => {
                                    const key = shortToLongDayMap[day];
                                    const available = row[key];
                                    const notes = !!row[`${key}_notes`];
                                    return (
                                        <td key={i} className="px-4 py-2 border-r text-center">
                                            <div className="flex flex-col items-center">
                                                {available ? (
                                                    <CheckCircle className="text-green-500 w-5 h-5" />
                                                ) : (
                                                    <XCircle className="text-red-300 w-5 h-5" />
                                                )}
                                                {notes && <StickyNote className="w-3 h-3 text-[#5D1F42] mt-1" />}
                                                <span className="text-xs text-gray-400 mt-1">{day}</span>
                                            </div>
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            )}

            <div className="flex items-center justify-between">
                <Button variant="outline" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>
                    Previous
                </Button>
                <span>
                    Page {page} of {totalPages}
                </span>
                <Button variant="outline" disabled={page === totalPages} onClick={() => setPage((p) => p + 1)}>
                    Next
                </Button>
            </div>
        </div>
    );
}
